import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack, Text } from '@mantine/core';
import { useMounted } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CountrySelectControl } from '@/module/Nomen/Country/CountrySelectControl';

import {
  Content,
  DateInputControl,
  Form,
  FormSubmit,
  NumberInputControl,
  SwitchControl,
  TextInputControl,
} from '@/core/components';

import { CurrencySelectControl, DAYJS_SERVER_DATE_FORMAT, useClearOnChange } from '@feature/utils';

import {
  clearHiddenByAbsenceMotivation,
  clearHiddenByActivityType,
  clearHiddenByPresentsPaper,
  useTravelFormDayBalancePreview,
  useTravelFormPaperTitleCheck,
} from './TravelRequestFormUtils';
import { TravelBlockingRequestsAlert } from './components/TravelErasmusBlocking';
import { TravelPaperTitleAlert } from './components/TravelPaperTitleAlert';
import { AbsenceMotivationSelectControl } from './components/form/AbsenceMotivationSelectControl';
import { ErasmusTypeSelectControl } from './components/form/ErasmusTypeSelectControl';
import { TeachingCoverageModeSelectControl } from './components/form/TeachingCoverageModeSelectControl';
import { TravelAbsenceDayFields } from './components/form/TravelAbsenceDayFields';
import { TravelActivityTypeCards } from './components/form/TravelActivityTypeCards';
import { TravelDayBalanceCard } from './components/form/TravelDayBalanceCard';
import { TravelDurationText, TravelEventPeriodText } from './components/form/TravelDurationText';
import { TravelExpenseListControl } from './components/form/TravelExpenseListControl';
import { TravelMobilitySupportCard } from './components/form/TravelMobilitySupportCard';
import { TravelRequiredFieldsNote } from './components/form/TravelRequiredFieldsNote';
import { getTravelUnfinishedErasmusOptions, getTravelUnfinishedReturnsOptions } from './model/TravelRequestQuery';
import { TravelRequestSchema } from './model/TravelRequestSchema';
import {
  TravelRequest,
  absenceMotivationTakesLeave,
  teachingCoverageProofs,
  travelDestinationInstitutionField,
} from './model/TravelRequestTypes';

interface TravelRequestFormProps extends FormSubmit<TravelRequestSchema> {
  title: string;
  // Set on edit, so the day balance preview and the paper title check leave the request itself out.
  requestId?: number | null;
  // Set on edit, where a numbered request keeps the activity types of its own registry.
  request?: TravelRequest | null;
  // The paper title a save was refused for, shown as taken until the title changes.
  refusedPaperTitle?: string | null;
  withRequiredNote?: boolean;
}

const erasmusGrantCurrencies = ['EUR'] as const;

export function TravelRequestForm(props: TravelRequestFormProps) {
  const {
    title,
    requestId = null,
    request = null,
    refusedPaperTitle = null,
    withRequiredNote = false,
    schema,
    defaultValues,
    values,
    isSubmitting,
  } = props;
  const { onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<TravelRequestSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });
  const { control, getValues, handleSubmit, setValue, watch } = form;

  // useWatch subscribes after the effect that applies `values`, so an edited request would paint the wrong branch.
  const activityType = watch('activityType');
  const presentsPaper = watch('presentsPaper');
  const absenceMotivation = watch('absence.absenceMotivation');
  const teachingCoverageMode = watch('absence.teachingCoverageMode');
  const withMobilitySupport = (watch('expenses') ?? []).some(
    expense => expense.expenseType === 'UMFST_MOBILITY_SUPPORT',
  );
  const departureDate = watch('departureDate');
  const eventStartDate = watch('eventStartDate');
  const owesCoverageProof = teachingCoverageMode != null && teachingCoverageProofs[teachingCoverageMode] != null;
  const today = dayjs().format(DAYJS_SERVER_DATE_FORMAT);

  useClearOnChange(activityType, next => clearHiddenByActivityType(next, setValue, getValues));
  useClearOnChange(presentsPaper, next => clearHiddenByPresentsPaper(next, setValue));
  useClearOnChange(absenceMotivation, next => clearHiddenByAbsenceMotivation(next, setValue));

  const isErasmus = activityType === 'ERASMUS';
  const isScientificEvent = activityType === 'SCIENTIFIC_EVENT';
  const isProfessionalDevelopment = activityType === 'PROFESSIONAL_DEVELOPMENT';

  const dayBalance = useTravelFormDayBalancePreview(watch, requestId);
  const dayTotals = dayBalance.totals;
  const paperTitleCheck = useTravelFormPaperTitleCheck(watch, requestId);

  const unfinishedErasmus = useQuery({ ...getTravelUnfinishedErasmusOptions(), enabled: isErasmus, retry: false });
  // `values` lands one render after mount, so an edited Erasmus request starts on the default type.
  const typeSettled = useMounted() || values == null;
  const unfinishedReturns = useQuery({
    ...getTravelUnfinishedReturnsOptions(),
    enabled: !isErasmus && typeSettled,
    retry: false,
  });
  const blocking = isErasmus ? unfinishedErasmus : unfinishedReturns;
  // Only the Erasmus gate hides the form: an overdue earlier trip is held back at submit, so its drafts stay editable.
  const blocked = isErasmus && (unfinishedErasmus.isPending || (unfinishedErasmus.data?.length ?? 0) > 0);

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        withoutSubmit={blocked}
        onCancel={onCancel}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TravelActivityTypeCards
              name="activityType"
              control={control}
              label={t('travel.requests.form.fields.activityType')}
              isSubmitting={isSubmitting}
              lockedRegistryType={request?.registration?.registryType ?? null}
              withAsterisk
            />
            {(isErasmus || typeSettled) && blocking.data != null && (
              <TravelBlockingRequestsAlert
                requests={blocking.data}
                rule={isErasmus ? 'erasmusCertificatePending' : 'earlierReturnPending'}
              />
            )}
          </Content>
          {!blocked && (
            <>
              <Content
                type="full"
                title={t('travel.requests.form.sections.general')}
              >
                {withRequiredNote && <TravelRequiredFieldsNote />}
                {isErasmus && (
                  <ErasmusTypeSelectControl
                    name="erasmusType"
                    control={control}
                    label={t('travel.requests.form.fields.erasmusType')}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                )}
                {!isErasmus && (
                  <TextInputControl
                    name="name"
                    control={control}
                    label={t(`travel.requests.form.fields.nameByType.${activityType}`)}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                )}
                <TextInputControl
                  name="destinationInstitution"
                  control={control}
                  label={t(`travel.requests.form.fields.${travelDestinationInstitutionField(activityType)}`)}
                  isSubmitting={isSubmitting}
                  withAsterisk
                />
                <Group align="flex-start">
                  <CountrySelectControl
                    name="country"
                    control={control}
                    label={t('travel.requests.form.fields.country')}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                  <TextInputControl
                    name="city"
                    control={control}
                    label={t('travel.requests.form.fields.city')}
                    description={t('travel.requests.form.cityHint')}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                </Group>
                <Group align="flex-start">
                  <DateInputControl<TravelRequestSchema>
                    name="departureDate"
                    label={t('travel.requests.form.fields.departureDate')}
                    description={t('travel.requests.form.tripDatesHint')}
                    minDate={today}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                  <DateInputControl<TravelRequestSchema>
                    name="returnDate"
                    label={t('travel.requests.form.fields.returnDate')}
                    description={t('travel.requests.form.tripDatesHint')}
                    minDate={departureDate ?? today}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                </Group>
                <TravelDurationText workingDays={dayTotals?.workingDays} />
                <Group align="flex-start">
                  <DateInputControl<TravelRequestSchema>
                    name="eventStartDate"
                    label={t('travel.requests.form.fields.eventStartDate')}
                    description={t('travel.requests.form.eventPeriodHint')}
                    minDate={departureDate ?? today}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                  <DateInputControl<TravelRequestSchema>
                    name="eventEndDate"
                    label={t('travel.requests.form.fields.eventEndDate')}
                    description={t('travel.requests.form.eventPeriodHint')}
                    minDate={eventStartDate ?? departureDate ?? today}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                </Group>
                <TravelEventPeriodText />
                {isScientificEvent && (
                  <SwitchControl
                    name="presentsPaper"
                    control={control}
                    label={t('travel.requests.form.fields.presentsPaper')}
                    isSubmitting={isSubmitting}
                  />
                )}
                {isScientificEvent && presentsPaper && (
                  <TextInputControl
                    name="paperTitle"
                    control={control}
                    label={t('travel.requests.form.fields.paperTitle')}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                )}
                {isScientificEvent && presentsPaper && (
                  <TravelPaperTitleAlert
                    check={paperTitleCheck}
                    refused={refusedPaperTitle != null && refusedPaperTitle === watch('paperTitle')?.trim()}
                  />
                )}
              </Content>
              {/* A leftover support line on another activity type stays visible here so it can be unticked. */}
              {(isProfessionalDevelopment || withMobilitySupport) && (
                <Content
                  type="full"
                  title={t('travel.requests.form.sections.mobilitySupport')}
                >
                  <TravelMobilitySupportCard isSubmitting={isSubmitting} />
                </Content>
              )}
              <Content
                type="full"
                title={t('travel.requests.form.sections.absence')}
              >
                {withRequiredNote && <TravelRequiredFieldsNote />}
                {!isErasmus && (
                  <TravelDayBalanceCard
                    preview={dayBalance.preview}
                    isLoading={dayBalance.isLoading}
                    isError={dayBalance.isError}
                    activityType={activityType}
                    absenceMotivation={absenceMotivation}
                    presentsPaper={presentsPaper}
                  />
                )}
                <Group align="flex-start">
                  <TeachingCoverageModeSelectControl
                    name="absence.teachingCoverageMode"
                    control={control}
                    label={t('travel.requests.form.fields.teachingCoverageMode')}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                  <TextInputControl
                    name="absence.teachingCoverageNote"
                    control={control}
                    label={t('travel.requests.form.fields.teachingCoverageNote')}
                    isSubmitting={isSubmitting}
                  />
                </Group>
                {teachingCoverageMode === 'COLLEAGUE_SUBSTITUTION' && (
                  <TextInputControl
                    name="absence.substitutePersonName"
                    control={control}
                    label={t('travel.requests.form.fields.substitutePersonName')}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                )}
                {owesCoverageProof && (
                  <Text
                    size="sm"
                    c="dimmed"
                  >
                    {t('travel.requests.form.recoveryProofHint')}
                  </Text>
                )}
                {!isErasmus && (
                  <AbsenceMotivationSelectControl
                    name="absence.absenceMotivation"
                    control={control}
                    label={t('travel.requests.form.fields.absenceMotivation')}
                    isSubmitting={isSubmitting}
                    allowedMotivations={dayTotals?.allowedMotivations}
                    activityType={activityType}
                    noCapDaysLeft={dayTotals != null && dayTotals.workingDays > 0 && dayTotals.capUsableDays === 0}
                    withMobilitySupport={withMobilitySupport}
                    withAsterisk
                  />
                )}
                {absenceMotivation != null && absenceMotivationTakesLeave(absenceMotivation) && (
                  <TravelAbsenceDayFields
                    control={control}
                    isSubmitting={isSubmitting}
                    activityType={activityType}
                    absenceMotivation={absenceMotivation}
                    totals={dayTotals}
                  />
                )}
                {absenceMotivation === 'OWN_PROGRAMS' && (
                  <TextInputControl
                    name="absence.ownProgramsMention"
                    control={control}
                    label={t('travel.requests.form.fields.ownProgramsMention')}
                    isSubmitting={isSubmitting}
                    withAsterisk
                  />
                )}
              </Content>
              <Content
                type="full"
                title={t('travel.requests.form.sections.funding')}
              >
                {!isErasmus && (
                  <SwitchControl
                    name="funding.perDiem"
                    control={control}
                    label={t('travel.requests.form.fields.perDiem')}
                    isSubmitting={isSubmitting}
                  />
                )}
                {!isErasmus && (
                  <TextInputControl
                    name="funding.fundingSourceNote"
                    control={control}
                    label={t('travel.requests.form.fields.fundingSourceNote')}
                    isSubmitting={isSubmitting}
                  />
                )}
                {isErasmus && (
                  <Group align="flex-start">
                    <NumberInputControl
                      name="funding.estimatedAmount"
                      control={control}
                      label={t('travel.requests.form.fields.estimatedAmount')}
                      isSubmitting={isSubmitting}
                      allowDecimal
                    />
                    {/* Shown but locked: an Erasmus grant is always EUR, and the payload pins it regardless. */}
                    <CurrencySelectControl
                      name="funding.estimatedAmountCurrency"
                      control={control}
                      label={t('travel.requests.form.fields.estimatedAmountCurrency')}
                      currencies={erasmusGrantCurrencies}
                      isSubmitting={isSubmitting}
                      isDisabled
                    />
                  </Group>
                )}
              </Content>
              {!isErasmus && !withMobilitySupport && (
                <Content type="full">
                  <TravelExpenseListControl
                    name="expenses"
                    control={control}
                    label={t('travel.requests.form.sections.expenses')}
                    description={t('travel.requests.form.expensesHint')}
                    isSubmitting={isSubmitting}
                    activityType={activityType}
                  />
                </Content>
              )}
            </>
          )}
        </Stack>
      </Form>
    </FormProvider>
  );
}
